import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generateTwoZoneV2 } from '@/lib/theme/generate-stylesheet';
import {
  createMerchantAsset,
  deleteMerchantAsset,
  uploadAssetFile,
} from '@/lib/api/merchant-assets';
import { updateService } from '@/lib/api/services';
import { serviceKeys } from '@/hooks/use-services';
import { merchantAssetKeys } from '@/hooks/use-merchant-assets';
import type { ThemeKnobs } from '@/lib/theme/theme-knobs';
import type { Service } from '@/types/service';

export interface SaveThemeVars {
  knobs: ThemeKnobs;
  /**
   * Issue #417: every save now creates a NEW stylesheet asset (a currentFileUrl PUT
   * would retire the old asset to status=replaced — terminal, vanishes from the
   * library). When this service was the SOLE user of its previous stylesheet asset,
   * the caller passes that asset id here to soft-delete it after the relink, so the
   * library doesn't accumulate orphans. A shared asset is left untouched (omit this).
   */
  retireAssetId?: string;
}

/** Stage where a partial failure happened — lets the UI explain that the service stayed unchanged. */
export type SaveThemeFailedStage = 'upload' | 'asset' | 'service';

export class SaveThemeError extends Error {
  constructor(
    readonly stage: SaveThemeFailedStage,
    readonly cause: unknown,
  ) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'SaveThemeError';
  }
}

/** Asset display name for a freshly forked/created stylesheet (≤200 per contract). */
function assetName(service: Service): string {
  const base = service.displayName?.trim() || service.serviceId;
  return `${base} – Theme`.slice(0, 200);
}

/**
 * Theme save-flow (Issue #341 / #417). Orchestrates the full chain:
 * generate two-zone-v2 CSS → upload as `text/css` → CREATE a new merchant stylesheet
 * asset → link it on the service (`theme.stylesheetAssetId`) → soft-delete the
 * predecessor when this service solely owned it. Every save creates a new asset
 * (Issue #417): a `currentFileUrl` PUT would retire the asset to status=replaced
 * (terminal, vanishes from the library) and the service would point at a dead asset.
 * The new asset id takes render priority, so any raw inline `stylesheetUrl` is left
 * as-is rather than cleared (clearing it with `''` would violate the Facade's
 * `format: uri` contract and 400 the PUT — Issue #416).
 *
 * Ordering makes partial failures safe: the service is only written after the asset
 * exists, so an upload- or asset-stage failure leaves the service untouched. Each
 * stage rethrows as a {@link SaveThemeError} so the UI can say which step failed.
 * The predecessor soft-delete is best-effort (a failure leaves an orphan, not a
 * broken service).
 */
export function useSaveTheme(service: Service | undefined) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, SaveThemeVars>({
    mutationFn: async ({ knobs, retireAssetId }) => {
      if (!service) throw new Error('No service loaded');
      const { merchantId, serviceId } = service;

      // 1. Generate the stylesheet from the live knobs + the stage-correct background.
      const css = generateTwoZoneV2(knobs, {
        backgroundUrl: service.theme?.backgroundImageUrl ?? null,
      });
      const file = new File([css], `${serviceId}-theme.css`, { type: 'text/css' });

      // 2. Upload the bytes; the CDN URL becomes the asset's currentFileUrl.
      let cdnUrl: string;
      try {
        cdnUrl = await uploadAssetFile(
          { merchantId, category: 'stylesheet', fileName: file.name, contentType: 'text/css' },
          file,
        );
      } catch (err) {
        throw new SaveThemeError('upload', err);
      }

      // 3. Always create a NEW asset (Issue #417 — never reuse via currentFileUrl PUT).
      let assetId: string;
      try {
        const created = await createMerchantAsset({
          merchantId,
          name: assetName(service),
          type: 'stylesheet',
          currentFileUrl: cdnUrl,
        });
        assetId = created.assetId;
      } catch (err) {
        throw new SaveThemeError('asset', err);
      }

      // 4. Link the new asset on the service. The new stylesheetAssetId takes render
      //    priority, so the raw inline URL needs no explicit clearing — and clearing
      //    it would break the save: `stylesheetUrl: ''` is not a valid `format: uri`
      //    (contract v2.9, non-nullable) → the Facade rejects the PUT with 400
      //    (Issue #416). Omit the field entirely; the stored URL stays untouched via
      //    partial-update and remains a valid URI. Last step on purpose — until here
      //    the service is unchanged.
      const { stylesheetUrl: _droppedInlineUrl, ...restTheme } = service.theme ?? {};
      try {
        await updateService(serviceId, {
          theme: { ...restTheme, stylesheetAssetId: assetId },
        });
      } catch (err) {
        throw new SaveThemeError('service', err);
      }

      // 5. Retire the predecessor asset when this service solely owned it (best-effort,
      //    non-fatal). The service now points at the new asset, so a failed delete
      //    only leaves an orphan in the library — never a broken service.
      if (retireAssetId && retireAssetId !== assetId) {
        try {
          await deleteMerchantAsset(retireAssetId, merchantId);
        } catch {
          /* non-fatal: orphan, not a broken service */
        }
      }
    },
    onSuccess: () => {
      if (!service) return;
      queryClient.invalidateQueries({ queryKey: serviceKeys.detail(service.serviceId) });
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: merchantAssetKeys.lists(service.merchantId) });
    },
  });
}
